import React, { useState, useEffect } from "react";
import "../../styles/Reports.scss";
import Modal from "react-modal";
import Select from "react-select";
import {
  getAllClients,
  getDailyReport,
  getUsersByRoleId,
} from "../../services/api";
import ExcelJS from "exceljs";
import moment from "moment";

Modal.setAppElement("#root");

const ITEMS_PER_PAGE = 10;

function DailyReport() {
  const [clients, setClients] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportData, setReportData] = useState([]);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleExport = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Daily Report");

    // Add header row
    worksheet.columns = [
      { header: "S.NO", key: "sno" },
      { header: "Recruiter", key: "recruiter_name" },
      { header: "No.of Submissions", key: "no_of_submissions" },
      { header: "Client", key: "client_name" },
      {
        header: "No. of Requirements Worked",
        key: "no_of_requirements_worked",
      },
      // { header: "Candidates Joined", key: "candidates_joined" },
    ];

    // Add data rows
    reportData.forEach((item, index) => {
      worksheet.addRow({
        sno: index + 1,
        recruiter_name: item.recruiter_name || "-",
        no_of_submissions: item.no_of_submissions || "-",
        client_name: item.client_name || "-",
        no_of_requirements_worked: item.no_of_requirements_worked || "-",
      });
    });

    // Create a Blob from the workbook
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "DailyReport.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pageCount) return;
    setPage(newPage);
  };

  // Reset to page 1 on search
  React.useEffect(() => {
    setPage(1);
  }, [selectedClient, startDate, endDate, reportData.length]);

  // Convert recruiters to react-select options
  const recruiterOptions = recruiters.map((recruiter) => ({
    value: recruiter.id,
    label: recruiter.name,
  }));

  // for final summary filter along with pagination
  const filteredData = reportData;

  const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  const paginatedData = filteredData.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  // To get All clients
  const fetchClients = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getAllClients();
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        setClients(res.result_info);
      } else {
        setError(res?.error_info || "Failed to fetch clients");
        setClients([]);
      }
    } catch (err) {
      setError("Failed to fetch clients. Please try again.");
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  // To get final summary data
  const fetchDailyReport = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getDailyReport(
        selectedClient?.value || "",
        selectedRecruiter?.value || "",
        moment(new Date("")).format("YYYY-MM-DD"),
        moment(new Date()).format("YYYY-MM-DD")
        
      );
      if (res && res.status === "S" && Array.isArray(res.result_info)) {
        setReportData(res.result_info);
      } else {
        setError(res?.message || "Failed to fetch report data");
        setReportData([]);
      }
    } catch (err) {
      setError("Failed to fetch report data. Please try again.");
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch clients on component mount
  useEffect(() => {
    fetchClients();
  }, []);

  // Fetch recruiters for role_id 4 (Recruiter)
  useEffect(() => {
    setLoading(true);
    const fetchRecruiters = async () => {
      setRecruiters([]);
      try {
        const res = await getUsersByRoleId(4);
        if (res?.status === "S" && Array.isArray(res.result_info)) {
          setRecruiters(res.result_info);
        }
      } catch (error) {
        console.error("Error fetching recruiters:", error);
        setError("Failed to fetch recruiters");
      } finally {
        setLoading(false);
      }
    };
    fetchRecruiters();
  }, []);

  // Fetch report data when client or dates change
  useEffect(() => {
    fetchDailyReport();
  }, [selectedClient,selectedRecruiter, startDate, endDate]);

  // Convert clients to react-select options
  const clientOptions = clients.map((client) => ({
    value: client.id,
    label: client.name,
  }));

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

   function getPageNumbers(currentPage, totalPages, maxPagesToShow = 5) {
    const pages = [];

    const half = Math.floor(maxPagesToShow / 2);
    let start = Math.max(currentPage - half, 1);
    let end = start + maxPagesToShow - 1;

    if (end > totalPages) {
      end = totalPages;
      start = Math.max(end - maxPagesToShow + 1, 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  }

  return (
    <div className="reports-page">
      <div className="reports-header-row">
        <div className="reports-title-group">
          <h1>Daily Report</h1>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button className="export-btn" onClick={handleExport}>
            Export
          </button>
        </div>
      </div>
      <div className="reports-table-controls">
        <div className="filters-row">
          <div className="select-wrapper">
            <div className="d-flex justify-content-center gap-2">
              <Select
                placeholder="Select a Client"
                value={selectedClient}
                onChange={setSelectedClient}
                options={clientOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: 220,
                    borderRadius: 6,
                    fontSize: "1rem",
                    background: "#fff",
                    border: "1.5px solid #d1d5db",
                  }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
                isClearable
                isSearchable
              />
              <Select
                placeholder="Select a Recruiter"
                value={selectedRecruiter}
                onChange={setSelectedRecruiter}
                options={recruiterOptions}
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: 220,
                    borderRadius: 6,
                    fontSize: "1rem",
                    background: "#fff",
                    border: "1.5px solid #d1d5db",
                  }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
                isClearable
                isSearchable
              />
            </div>
          </div>
          <div className="date-filters d-none">
            <div className="date-input-wrapper">
              {!startDate && (
                <span className="placeholder-text">Start Date</span>
              )}
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => {
                  const newStartDate = e.target.value;
                  
                  if (endDate && newStartDate > endDate) {
                    // alert('Start date cannot be later than end date');
                    return;
                  }
                  
                  setStartDate(newStartDate);
                }}
                className="date-input"
              />
            </div>

            <div className="date-input-wrapper">
              {!endDate && <span className="placeholder-text">End Date</span>}
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => {
                  const newEndDate = e.target.value;
                  
                  if (startDate && newEndDate < startDate) {
                    // alert('End date cannot be earlier than start date');
                    return;
                  }
                  
                  setEndDate(newEndDate);
                }}
                className="date-input"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="reports-table-wrapper">
        {loading ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            Loading report data...
          </div>
        ) : (
          <table className="reports-table" data-columns="5">
            <thead>
              <tr>
                <th>S.NO</th>
                <th>Recruiter</th>
                <th>No. of Submissions</th>
                <th>Client</th>
                <th>No. of Requirements Worked</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>
                    No reports found!
                  </td>
                </tr>
              ) : (
                paginatedData.map((item, index) => (
                  <tr key={item.correl_id || index}>
                    <td title={(page - 1) * ITEMS_PER_PAGE + index + 1}>{(page - 1) * ITEMS_PER_PAGE + index + 1}</td>
                    <td title={item.recruiter_name || "--"}>{item.recruiter_name || "--"}</td>
                    <td title={item.no_of_submissions || 0}>{item.no_of_submissions || 0}</td>
                    <td title={item.client_name || "--"}>{item.client_name || "--"}</td>
                    <td title={item.no_of_requirements_worked || 0}>{item.no_of_requirements_worked || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {/* Pagination Controls */}
       {pageCount > 1 && (
  <div className="settings-pagination">
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page - 1)}
      disabled={page === 1}
    >
      &lt;
    </button>
    {getPageNumbers(page, pageCount, 5).map((pageNum) => (
      <button
        key={pageNum}
        className={"pagination-btn" + (page === pageNum ? " active" : "")}
        onClick={() => handlePageChange(pageNum)}
      >
        {pageNum}
      </button>
    ))}
    <button
      className="pagination-btn"
      onClick={() => handlePageChange(page + 1)}
      disabled={page === pageCount}
    >
      &gt;
    </button>
  </div>
)}
    </div>
  );
}

export default DailyReport;
